import { useEffect, useMemo, useState } from "react";
import { marginText } from "../components/shopify/report/marginText";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { api } from "../api/apiClient";
import type { WebOrdersPersonReport, WebOrdersReportQuery } from "../api/types";
import { euro } from "../components/finances/opcostFormat";
import { OrderJourneySheet } from "../components/shopify/report/OrderJourneySheet";
import { OrderNumberLink } from "../components/shopify/report/OrderNumberLink";
import { ReportPeriodPicker } from "../components/shopify/report/ReportPeriodPicker";
import { queryParams } from "../components/shopify/report/reportLinks";
import { PART_LABEL, STATE_BADGE, linesText, whenText } from "../components/shopify/report/webOrdersText";
import { roleLabel } from "../components/team/teamLabels";
import { EmptyState } from "../components/ui/EmptyState";
import { StatTile } from "../components/ui/StatTile";
import { periodDays, periodFromParams, type ReportPeriodValue } from "../utils/reportPeriods";

/**
 * G-143 — one person's web orders: every order they accepted, built a
 * bouquet of, handed over or carried, and the money of the ones they handed
 * over (OD-221), commission included (OD-219). Cost is there only when the
 * server sent it — owners (OD-220). A manager reads their own shop's people.
 */
export function ShopifyOrdersPersonPage() {
  const { userId = "" } = useParams();
  const [searchParams] = useSearchParams();
  const shopId = searchParams.get("shop") ?? "";
  const [period, setPeriod] = useState<ReportPeriodValue>(() => periodFromParams(searchParams));
  const [report, setReport] = useState<WebOrdersPersonReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openOrderId, setOpenOrderId] = useState<string | null>(null);

  const days = periodDays(period);
  const from = days?.from;
  const to = days?.to;
  const query = useMemo<WebOrdersReportQuery | null>(
    () => (from && to ? { from, to, ...(shopId ? { locationId: shopId } : {}) } : null),
    [from, to, shopId],
  );

  useEffect(() => {
    if (!query || !userId) return;
    let cancelled = false;
    setError(null);
    api
      .getWebOrdersPerson(userId, query)
      .then((r) => {
        if (!cancelled) setReport(r);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : "Could not load this person's web orders");
      });
    return () => {
      cancelled = true;
    };
  }, [query, userId]);

  const back = useMemo(() => {
    const params = queryParams(query ?? {});
    params.set("tab", "by-shop");
    return `/app/shopify-orders?${params.toString()}`;
  }, [query]);

  const totals = report?.totals;
  const money = totals?.money ?? null;
  const showsCost = money?.costEur !== undefined;
  const shopNames = report
    ? Array.from(new Set(report.orders.map((o) => o.shop?.name).filter((n): n is string => Boolean(n))))
    : [];
  const moneyColumns = showsCost ? 4 : 3;

  return (
    <section className="wo-report">
      <Link to={back} className="wo-back">
        Web orders by shop
      </Link>
      <div className="page-header">
        <div>
          <h2 className="page-title">{report?.person.fullName ?? "Web orders"}</h2>
          {report ? (
            <p className="page-subtitle">
              {[roleLabel(report.person.role), ...shopNames].join(" · ")}
            </p>
          ) : null}
        </div>
      </div>

      <div className="card an-filters">
        <ReportPeriodPicker value={period} onChange={setPeriod} />
      </div>

      {error ? <div className="alert error">{error}</div> : null}

      {query && report && totals ? (
        <>
          <div className="stat-tile-row">
            <StatTile
              label="Web orders worked on"
              value={totals.orders}
              hint={[
                `${totals.accepted} accepted`,
                `${totals.bouquetsBuilt} built`,
                `${totals.handedOver} handed over`,
                ...(totals.delivered ? [`${totals.delivered} delivered`] : []),
              ].join(" · ")}
            />
            <StatTile label="Booked from own hand-overs" value={euro(money?.bookedEur ?? "0")} />
            {showsCost ? <StatTile label="Cost of goods" value={euro(money?.costEur)} /> : null}
            <StatTile
              label="Margin"
              value={marginText(money)}
              hint={money ? `after ${euro(money.vatEur)} goods IVA · before commission` : undefined}
            />
            <StatTile label="Commission" value={euro(money?.commissionEur ?? "0")} />
          </div>

          {report.orders.length === 0 ? (
            <div className="card">
              <EmptyState title="No web orders in this period" />
            </div>
          ) : (
            <section className="card an-card" aria-labelledby="wo-person-orders">
              <div className="an-card-head">
                <h3 id="wo-person-orders">Web orders</h3>
              </div>
              <div className="table-scroll">
                <table className="wo-table">
                  <thead>
                    <tr>
                      <th>Received</th>
                      <th>Order</th>
                      <th>What was in it</th>
                      <th>Part</th>
                      <th className="wo-num">Booked</th>
                      {showsCost ? <th className="wo-num">Cost of goods</th> : null}
                      <th className="wo-num">Margin</th>
                      <th className="wo-num">Commission</th>
                      <th>
                        <span className="visually-hidden">Details</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.orders.map((row) => (
                      <tr key={row.id}>
                        <td className="wo-nowrap" data-label="Received">{whenText(row.arrivedAt)}</td>
                        <td className="wo-cell-title">
                          <OrderNumberLink row={row} />
                        </td>
                        <td data-label="What was in it">{linesText(row) || "—"}</td>
                        <td data-label="Part">
                          <span className="wo-parts">
                            {row.parts.map((part) => (
                              <span key={part} className="badge muted">
                                {PART_LABEL[part]}
                              </span>
                            ))}
                          </span>
                        </td>
                        {row.money ? (
                          <>
                            <td className="wo-num" data-label="Booked">{euro(row.money.bookedEur)}</td>
                            {showsCost ? (
                              <td className="wo-num" data-label="Cost of goods">{euro(row.money.costEur)}</td>
                            ) : null}
                            <td className="wo-num" data-label="Margin">{marginText(row.money)}</td>
                            <td className="wo-num" data-label="Commission">{euro(row.money.commissionEur)}</td>
                          </>
                        ) : (
                          <td colSpan={moneyColumns} className="muted" data-label="Booked">
                            {row.bookedTo ? `Booked to ${row.bookedTo.fullName}` : STATE_BADGE[row.state].label}
                          </td>
                        )}
                        <td>
                          <button type="button" className="wo-link" onClick={() => setOpenOrderId(row.id)}>
                            Details
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  {money ? (
                    <tfoot>
                      <tr>
                        <td colSpan={4}>
                          {totals.orders} orders · {totals.handedOver} handed over
                        </td>
                        <td className="wo-num">{euro(money.bookedEur)}</td>
                        {showsCost ? <td className="wo-num">{euro(money.costEur)}</td> : null}
                        <td className="wo-num">{marginText(money)}</td>
                        <td className="wo-num">{euro(money.commissionEur)}</td>
                        <td />
                      </tr>
                    </tfoot>
                  ) : null}
                </table>
              </div>
            </section>
          )}
        </>
      ) : null}

      {openOrderId ? <OrderJourneySheet orderId={openOrderId} onClose={() => setOpenOrderId(null)} /> : null}
    </section>
  );
}
